/**
 * 学生端 · 今日打卡 —— 全站体验最好的页面（见契约 2.1、2.3）。
 *
 * 主路径：分组清单 → 点「开始」填预计时长 → 计时中（已用时间 / 暂停 / 继续 / 放弃）
 *        → 点「完成」（可填量化值与一句备注）。
 *
 * ## 「放弃」的三道防误触（2026-10-17 用户实测）
 *
 * > 孩子说想点完成时，**不小心点了放弃**，这个有什么方案？
 *
 * 1. **位置**：从「完成」正右边挪到卡片底部单独一栏的行尾，退化成灰色文字按钮；
 * 2. **二次确认**：点它只弹确认（`lib/skipUndo.ts` 的 reducer，打开/取消零请求），
 *    文案写清"今天就结束了"，**默认焦点在"还没完，继续做"上**，
 *    危险键降到下方单独一行、尺寸小一档；
 * 3. **可撤销**：放弃成功立刻弹带「撤销」按钮的 toast（15 秒），
 *    当天之内那一行还一直有「恢复成未完成」（`POST /checkins/:id/restore`）。
 *
 * 三条硬要求都在这里落地：
 *  1. **计时显示基于服务端时间戳**（`useServerTimer`，用单调时钟推进，绝不本地累加）。
 *  2. **计时中每 30 秒心跳一次** `/checkins/:id/heartbeat`（`useServerTimer` 内部负责）。
 *  3. **儿童模式**（`gradeToUiMode`）下：≥56px 点击区、≥18px 字号、图标+文字、
 *     隐藏统计图表与导出、隐藏多级树（只显示"今天要做什么"）、**打卡最多 2 步**。
 *
 * 权限（契约 3）：家长创建的内容对学生只读，界面上这些条目显示「家长创建，只能打卡」，
 * 并且**不渲染任何编辑/删除入口** —— 判定走 shared 的 `canMutateContent`。
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { CheckinTimerPanel } from '../../components/ui/CheckinTimerPanel';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState, iconForName, statusMeta } from '../../components/ui/Bits';
import { useCheckinTimerMutations, useToday } from '../../lib/domainQueries';
import { useMyStudent } from '../../lib/useMyStudent';
import { useAuth } from '../../lib/auth';
import { useStudentActor } from '../../lib/ActorContext';
import {
  canDoCheckin,
  isChildMode,
  readOnlyHint,
  resolveUiMode,
  uiModeHint,
} from '../../lib/uiMode';
import { newIdempotencyKey } from '../../lib/api/client';
import { toErrorMessage } from '../../lib/api/error';
import { groupTodayItems, uniqueTodayItems } from '../../lib/todayGroups';
import { primeEstimateChimeAudio } from '../../lib/estimateChimeAudio';
import { cn, formatClock, formatPlannedDuration } from '../../lib/utils';
import { calculateCheckinPoints } from '@fht/shared';
import {
  defaultStartEstimate,
  startEstimatePresets,
  type StartEstimate,
} from '@fht/shared';
import {
  INITIAL_SKIP_FLOW,
  RESTORE_ENTRY,
  SKIP_CONFIRM_COPY,
  SKIP_UNDO_TOAST_MS,
  canShowRestoreEntry,
  reduceSkipFlow,
  restoreSuccessMessage,
  type SkipFlowState,
} from '../../lib/skipUndo';
import {
  checkFinishAmount,
  finishAmountHint,
  finishAmountLabel,
} from '../../lib/finishAmount';
import type { TodayTaskDetail } from '../../lib/api/types';
import { StudentDictationEntry } from '../../components/dictation/StudentDictationEntry';

/** 预计时长快选（分钟）。儿童模式只显示前几项，避免选择过多。 */
const DURATION_PRESETS = [10, 15, 20, 30, 45, 60] as const;

/**
 * 完成弹窗的目标任务。
 *
 * `mode: 'makeup'` = 走**补卡**（漏打宽限期内）；两者共用同一个弹窗与同一套
 * "实际完成量"校验（`lib/finishAmount.ts`），只是文案与接口不同。
 */
interface FinishTarget {
  task: TodayTaskDetail;
  mode: 'finish' | 'makeup';
}

/**
 * ⚠️ 这里**曾经**有一个 `toCheckin()` 把今日任务映射成 `Checkin` 形状喂给计时 hook。
 * 现在 `CheckinTimerPanel` 只收 `checkinId / status / actualSeconds / estimatedSeconds`
 * 四个字段，那一层映射被删掉了 —— 「不造字段」因此是**结构**上的保证
 * （面板不解析 `startedAt`：本地时间不可信，见 `useServerTimer` 的注释）。
 */

// ---------------------------------------------------------------- 单条任务

interface TaskRowProps {
  task: TodayTaskDetail;
  kid: boolean;
  busy: boolean;
  actor: ReturnType<typeof useStudentActor>;
  /**
   * 家庭时区的今天（`/student/today` 的 `localDate`）。
   *
   * ⚠️ 只用于一件事：判断这条 `skipped` 是不是**今天**的（孩子只能撤销当天的放弃，
   * 见 `lib/skipUndo.ts` 的 `canShowRestoreEntry`）。不做任何时间计算。
   */
  today: string | null;
  onStart: (task: TodayTaskDetail) => void;
  onFinish: (task: TodayTaskDetail) => void;
  onResume: (task: TodayTaskDetail) => void;
  onPause: (task: TodayTaskDetail) => void;
  onSkip: (task: TodayTaskDetail) => void;
  /** 撤销放弃（`skipped → pending`）—— 误点「放弃」的那条回程。 */
  onRestore: (task: TodayTaskDetail) => void;
  onMakeup: (task: TodayTaskDetail) => void;
}

function TaskRow({
  task,
  kid,
  busy,
  actor,
  today,
  onStart,
  onFinish,
  onResume,
  onPause,
  onSkip,
  onRestore,
  onMakeup,
}: TaskRowProps) {
  const meta = statusMeta(task.status);
  const isRunning = task.status === 'running';
  const isPaused = task.status === 'paused' || task.status === 'auto_paused';
  const isDone = task.status === 'done';
  const isSkipped = task.status === 'skipped';
  const canCheckin = canDoCheckin(actor);

  /**
   * 这一行要不要给「恢复成未完成」。
   *
   * 判据在 `lib/skipUndo.ts` 的 `canShowRestoreEntry()`（纯函数、有单测），
   * 它是服务端 `decideRestore()` 的镜像 —— 真正的门禁在 API 那边。
   *
   * ⚠️ `isHomework: false` 是**事实，不是偷懒**：`GET /student/today` 的 SQL 里
   *    已经 `ne(nodes.kind, 'homework')`（见 `getToday()`），这一页**不可能**出现作业项。
   *    作业项的「放弃」与「撤销」都只在家长端（`ParentHomeworkPage`）。
   * ⚠️ `dueDate: today` 同理：这一页只查 `due_date = 今天` 的行，
   *    所以"这条属于哪一天"的答案就是 `today` 本身。
   */
  const showRestore = canShowRestoreEntry({
    status: task.status,
    isHomework: false,
    actorKind: actor.kind === 'student' ? 'student' : 'parent',
    dueDate: today,
    today,
  });

  /**
   * ⚠️ 计时面板与"到点提示音"都在 `CheckinTimerPanel` 里（与作业页的三条队列
   * 共用同一个组件）：本行**不再**自己调 `useServerTimer` / `useEstimateChime`。
   * `isRunning / isPaused` 只用于卡片描边与按钮，不再参与计时。
   */

  // 学生只能打卡，不能改删家长创建的内容 —— 界面直接给出说明
  const hint = readOnlyHint(actor, {
    createdByType: task.createdByType ?? 'parent',
    createdById: task.createdById ?? '',
  });

  /**
   * 这条任务"完成可得多少分"。
   *
   * ⚠️ 走 **shared 的同一个算法**，不在这里手写乘法：规则是
   * `floor(pointsPerUnit × 目标值 ÷ pointsUnitSize)`（见方案 3.4.1），
   * 少乘/漏除 N 都会让卡片显示一个后端根本不会给的数字。
   * 没设量化目标时按"一次"计（忽略 N），与后端同一套语义。
   */
  const points = calculateCheckinPoints({
    pointsPerUnit: task.pointsPerUnit ?? 0,
    unitSize: task.pointsUnitSize,
    targetValue: task.targetValue ?? null,
    valueDone: task.targetValue ?? null,
  });

  return (
    <li>
      <div
        className={cn(
          'rounded-2xl border bg-white',
          kid ? 'border-2 p-4' : 'border-slate-200 p-3',
          isRunning && 'border-brand-400 ring-2 ring-brand-100',
          isDone && 'bg-emerald-50/60',
        )}
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'flex shrink-0 items-center justify-center rounded-2xl bg-brand-50',
              kid ? 'h-14 w-14 text-3xl' : 'h-11 w-11 text-xl',
            )}
          >
            {iconForName(task.name, isDone ? '✅' : '📘')}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className={cn('font-semibold text-slate-900', kid ? 'text-xl' : 'text-base')}>
                {task.name}
              </p>
              <Badge tone={meta.tone}>
                {meta.icon} {meta.label}
              </Badge>
              {task.isMakeup && <Badge tone="warn">补卡</Badge>}
            </div>

            <p className={cn('mt-1 text-slate-500', kid ? 'text-base' : 'text-xs')}>
              {[
                task.goalName ? `属于：${task.goalName}` : null,
                task.targetValue ? `目标 ${task.targetValue}${task.unit ?? ''}` : null,
                // 这是**计划时长**（孩子自己填的预计），不是实际用时 —— 显示到
                // 分钟就够，所以走 formatPlannedDuration 而不是 shared 的
                // formatDuration（后者给 actualSeconds 用，见 lib/utils.ts）
                task.estimatedSeconds ? `预计 ${formatPlannedDuration(task.estimatedSeconds)}` : null,
                points > 0 ? `完成可得 ${points} 分` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>

            {/* 归属说明：家长创建的只能打卡（契约 3） */}
            {hint && (
              <p className={cn('mt-1 text-amber-700', kid ? 'text-sm' : 'text-xs')}>🔒 {hint}</p>
            )}

            {/* 计时中/暂停中：大号已用时间 */}
            {/* ⚠️ 与「今天的作业」/「超时」/「还有效」是**同一个**组件
                （`components/ui/CheckinTimerPanel.tsx`）：数字格式、状态说明、
                心跳失败提示、到点提示音都只有一份实现。 */}
            <CheckinTimerPanel
              checkinId={task.checkinId}
              status={task.status}
              actualSeconds={task.actualSeconds ?? 0}
              estimatedSeconds={task.estimatedSeconds ?? null}
              kid={kid}
              tone="brand"
              live
            />

            {isDone && (
              <p className={cn('mt-1 text-emerald-700', kid ? 'text-base' : 'text-xs')}>
                ✅ 完成
                {task.valueDone !== null && task.valueDone !== undefined
                  ? ` ${task.valueDone}${task.unit ?? ''}`
                  : ''}
                {task.note ? ` · ${task.note}` : ''}
                {task.points ? ` · 得 ${task.points} 分` : ''}
              </p>
            )}
          </div>
        </div>

        {/*
          ---------- 操作区（主按钮行） ----------
          ⚠️ 这里**只剩**「开始 / 暂停·完成 / 继续·完成 / 补卡」。
          「放弃」被移到了下面单独一栏（见 `放弃（次要动作）`）——
          2026-10-17 用户实测：孩子想点「完成」，**误触了正右边的「放弃」**。
          主按钮行里不再有任何"危险动作"，是这次修复的一半。
        */}
        {canCheckin && (
          <div className={cn('mt-3 flex flex-wrap gap-2', kid && 'gap-3')}>
            {task.status === 'pending' && (
              <Button
                size={kid ? 'kid' : 'md'}
                variant={kid ? 'kid' : 'primary'}
                disabled={busy}
                icon={<span aria-hidden="true">▶️</span>}
                onClick={() => onStart(task)}
              >
                开始
              </Button>
            )}

            {isRunning && (
              <>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant="secondary"
                  disabled={busy}
                  icon={<span aria-hidden="true">⏸️</span>}
                  onClick={() => onPause(task)}
                >
                  暂停
                </Button>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid-green' : 'primary'}
                  disabled={busy}
                  icon={<span aria-hidden="true">✅</span>}
                  onClick={() => onFinish(task)}
                >
                  完成
                </Button>
              </>
            )}

            {isPaused && (
              <>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid' : 'primary'}
                  disabled={busy}
                  icon={<span aria-hidden="true">▶️</span>}
                  onClick={() => onResume(task)}
                >
                  继续
                </Button>
                <Button
                  size={kid ? 'kid' : 'md'}
                  variant={kid ? 'kid-green' : 'primary'}
                  disabled={busy}
                  onClick={() => onFinish(task)}
                >
                  完成
                </Button>
              </>
            )}

            {/* 漏打：宽限期内可补卡（后端用 canMakeup 标记） */}
            {(task.status === 'missed' || task.status === 'skipped') && task.canMakeup && (
              <Button
                size={kid ? 'kid' : 'md'}
                variant="secondary"
                disabled={busy}
                icon={<span aria-hidden="true">🕘</span>}
                onClick={() => onMakeup(task)}
              >
                补卡
              </Button>
            )}
          </div>
        )}

        {/*
          ---------- 次要动作（与主按钮行**分开的一栏**） ----------

          为什么单独一栏而不是挤在主按钮行里（2026-10-17 用户实测的原话）：
          > "孩子说想点完成时，**不小心点了放弃**"
          截图里「放弃」就紧贴「完成」右边、同一个容器 —— 手指落在两个按钮之间，
          任何一个方向都会中一个。现在它被移到**分隔线下方的行尾**，并且：
            · 退化成**文字按钮**（灰字、无边框、无填充），不再是"看起来一样重"的按钮；
            · 尺寸降一档但守住触摸区下限（`min-h-[44px]`，儿童模式 48px；
              直接按 56px 会又大又显眼，那正是误触的来源）；
            · 文案带省略号「放弃…」，提示后面还有一步确认。
          ⚠️ 这里用原生 `<button>` 而不是 `<Button variant="ghost">`：
             `ghost` 的文字是 brand 蓝（`text-brand-700`），和"危险动作"的观感不符；
             而 `cn()` 只是字符串拼接，追加 `text-slate-500` 并不能可靠覆盖
             （同优先级类的胜负由样式表里的顺序决定），所以这里直接写死样式。
          ⚠️ 它**没有**被藏进"更多"菜单：这是正常功能（今天这次不做了），
             藏到找不到等于把一个用户明确要保留的能力删掉。
        */}
        {/*
          ⚠️ 条件是"里面真的有东西要渲染"（`showRestore` 而不是 `isSkipped`）：
             否则一条**不能**恢复的 skipped（比如隔天的）会留下一条空的横线。
        */}
        {canCheckin && (isRunning || isPaused || showRestore) && (
          <div
            className={cn(
              'mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-2',
              kid && 'gap-3 pt-3',
            )}
          >
            {(isRunning || isPaused) && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onSkip(task)}
                className={cn(
                  'rounded-lg font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                  'disabled:cursor-not-allowed disabled:text-slate-300',
                  kid ? 'min-h-[48px] px-4 text-base' : 'min-h-[44px] px-3 text-sm',
                )}
              >
                放弃…
              </button>
            )}

            {/*
              撤销放弃：**今天之内一直可用**（不是 15 秒）。
              15 秒那条限时入口是放弃成功后弹出的 toast（见页面里的 `confirmSkip`）；
              这里是从「已放弃」状态回来看见的那条常驻入口 ——
              孩子过半小时想起来"我刚才点错了"也能救回来。
              权限：孩子只能撤销自己今天的（服务端 `decideRestore` 把关）。
            */}
            {showRestore && (
              <Button
                size={kid ? 'kid' : 'sm'}
                variant="secondary"
                disabled={busy}
                icon={<span aria-hidden="true">↩️</span>}
                onClick={() => onRestore(task)}
              >
                {RESTORE_ENTRY.label}
              </Button>
            )}
          </div>
        )}

        {/* 误点说明只在「已放弃」这一行出现，而且只有当真能恢复时才说 */}
        {isSkipped && showRestore && (
          <p className={cn('mt-1 text-slate-400', kid ? 'text-sm' : 'text-xs')}>
            {RESTORE_ENTRY.hint}
          </p>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- 页面

/**
 * 入口包装：负责把 **auth_user.id 翻译成 student.id**（见 lib/useMyStudent.ts）。
 *
 * 解析成功后才渲染下面的界面 —— 这样下游组件拿到的 `studentId` 一定是对的 id 空间，
 * 杜绝运行期 404。
 */
export function StudentTodayPage() {
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="今日打卡">
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!my.studentId) {
    return (
      <AppShell kind="student" title="今日打卡">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }

  return <StudentTodayScreen studentId={my.studentId} />;
}

function StudentTodayScreen({
  studentId,
}: {
  /** **student.id**（学生档案主键），不是 auth_user.id。 */
  studentId: string;
}) {
  const toast = useToast();
  const actor = useStudentActor();
  // 用于判断"是否还在用家长给的初始密码"（见下方提醒条）
  const { user } = useAuth();

  const todayQuery = useToday(studentId);
  const mutations = useCheckinTimerMutations(studentId);

  // ---------- 儿童模式：按后端返回的 uiMode 判定（家长可在孩子档案里覆盖） ----------
  const mode = resolveUiMode({ serverUiMode: todayQuery.data?.uiMode ?? null });
  const kid = isChildMode(mode);

  const [durationTarget, setDurationTarget] = useState<TodayTaskDetail | null>(null);
  const [durationInput, setDurationInput] = useState('20');
  /**
   * 这次弹窗的预填值**从哪来**（家长目标 / 上次预计 / 兜底）。
   *
   * 只用于两件事：把快选按钮补上家长目标那一个；家长目标生效时在弹窗里写明
   * 「家长设的目标：30 分钟」，让孩子知道这个数字不是随便给的。
   * 判定与换算全在 `@fht/shared` 的 `start-estimate.ts`（唯一一份）。
   */
  const [durationEstimate, setDurationEstimate] = useState<StartEstimate | null>(null);
  const [finishTarget, setFinishTarget] = useState<FinishTarget | null>(null);
  /**
   * 「实际完成量」的输入内容 —— **起步永远是空串**（`openFinish` / `openMakeup` 都清空）。
   *
   * ⚠️ 以前这里会被预填成 `task.targetValue`，孩子顺手点「完成」就把"目标值"当成
   *   "实际完成量"记下来了（会算分、进统计）。判定与文案见 `lib/finishAmount.ts`。
   */
  const [finishValue, setFinishValue] = useState('');
  const [finishNote, setFinishNote] = useState('');
  /**
   * 「放弃」的弹窗状态 —— **唯一的入口是 `lib/skipUndo.ts` 的 reducer**。
   *
   * 为什么不是一个 `useState<task|null>`（这里原来就是）：
   *  - 两次确认不能被双击变成两次请求（reducer 的 `submitting` 阶段挡住了第二下）；
   *  - 打开确认框 / 改原因 / 取消 **一律不发请求**，只有 `confirm` 事件会产生副作用；
   *  - 这些不变式有单测（`lib/skipUndo.test.ts`），而埋在 JSX 里的 useState 测不到。
   */
  const [skipFlow, setSkipFlow] = useState<SkipFlowState>(INITIAL_SKIP_FLOW);
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * 今日清单（**已去重**）：同一个 `checkinId` 只算一条。
   *
   * ⚠️ 这是 2026-10-17 那个"孩子提交完成后出现重复打卡记录"的修法落点之一：
   *    进度条、小星星、分组全部读这一份去重视图，**列表卡片数与 `X / Y 项` 的
   *    Y 不可能对不上**。
   */
  const items = useMemo(() => uniqueTodayItems(todayQuery.data?.items ?? []), [todayQuery.data]);
  const notReady = todayQuery.data?.notReady ?? false;
  const pending = items.filter((task) => task.status !== 'done');
  const doneCount = todayQuery.data?.doneCount ?? items.length - pending.length;

  /**
   * ⚠️ 2026-10-30 改为**平铺**（不再按节点分组渲染）。
   *
   * 用户原话："我发现我对打卡任务排序后的任务排序，从孩子端界面的排序是不一样的。"
   *
   * 根因：这一页原来按 `nodeId` 分组，**组与组之间不能交错** —— 家长把
   * A 节点下的第 1 个任务、B 节点下的任务、再 A 节点下的第 2 个任务这样排，
   * 分组后会被强行并成"A 的（1、2）→ B 的"，于是家长拖出来的顺序在孩子端
   * 永远对不上。而卡面上本来就写着「属于：XXX」，分组标题是冗余的。
   *
   * 现在直接按服务端顺序（`ORDER BY habit.sort_order, …`）平铺 ——
   * **家长拖成什么样，孩子就按什么顺序看到**。
   * `lib/todayGroups.ts` 的纯函数与用例保留（仍是"按节点分组"的参考实现），
   * 只是这一页不再用它。
   */
  /** 已完成的那一叠（默认收起，见下面的渲染）。 */
  const doneItems = useMemo(() => items.filter((task) => task.status === 'done'), [items]);
  const [showDone, setShowDone] = useState(false);

  const busy =
    mutations.start.isPending ||
    mutations.pause.isPending ||
    mutations.resume.isPending ||
    mutations.finish.isPending ||
    mutations.skip.isPending ||
    mutations.restore.isPending ||
    mutations.makeup.isPending;

  /**
   * 渲染一张打卡卡（未完成的列表与"已完成"折叠区**共用**这一份）。
   *
   * ⚠️ 抽出来只为一件事：2026-10-30 加"已完成折叠"时，如果另抄一遍 TaskRow 的
   * props（十几个回调），两处迟早会漂移 —— 那正是本项目 2026-10-17 "计时数字消失"
   * 那个 bug 的形状（同一块 JSX 抄了两份，第三处没抄）。
   */
  const renderTaskRow = (task: TodayTaskDetail) => (
    <TaskRow
      key={task.checkinId}
      task={task}
      kid={kid}
      busy={busy}
      actor={actor}
      /* 家庭时区的今天：孩子只能撤销**今天**的放弃（见 lib/skipUndo.ts） */
      today={todayQuery.data?.localDate ?? null}
      onStart={(t) => {
        setDurationTarget(t);
        /**
         * 预填值：**家长设的目标时间 > 上一次的预计 > 20 分钟**
         * （判定在 `@fht/shared` 的 `start-estimate.ts`，两处弹窗同一份）。
         * 家长把任务设成「每天阅读 30 分钟」时，孩子点开始就该看到 30，而不是 20。
         */
        const estimate = defaultStartEstimate({
          targetValue: t.targetValue,
          unit: t.unit,
          estimatedSeconds: t.estimatedSeconds,
        });
        setDurationEstimate(estimate);
        setDurationInput(String(estimate.minutes));
        setActionError(null);
      }}
      onFinish={openFinish}
      onPause={(t) => void doPause(t)}
      onResume={(t) => void doResume(t)}
      onSkip={openSkip}
      onRestore={(t) => void doRestore(t.checkinId, t.name)}
      onMakeup={openMakeup}
    />
  );

  /** 开始计时：必须先填预计时长（服务端要求 estimatedSeconds，见方案 2.3）。 */
  const confirmStart = async () => {
    if (!durationTarget) return;
    /**
     * 借这次**真实的点击手势**解锁音频。
     *
     * 二十分钟后那一声提示音不在手势里，iOS 会静默拒绝播放 ——
     * 在这里把 `AudioContext` 建好并 resume() 是最稳的做法
     * （见 `lib/estimateChimeAudio.ts` 的文件头）。失败也无所谓：没有声音而已。
     */
    primeEstimateChimeAudio();
    const minutes = Number(durationInput);
    if (!Number.isFinite(minutes) || minutes < 1) {
      setActionError('预计时长至少 1 分钟');
      return;
    }
    setActionError(null);
    try {
      await mutations.start.mutateAsync({
        checkinId: durationTarget.checkinId,
        estimatedSeconds: Math.round(minutes * 60),
        // 一次用户意图一个幂等键：双击「开始」不会产生两条记录
        idempotencyKey: newIdempotencyKey(`start:${durationTarget.checkinId}`),
      });
      toast.showToast('开始计时，加油！', 'success');
      setDurationTarget(null);
    } catch (err) {
      setActionError(toErrorMessage(err));
    }
  };

  const doPause = async (task: TodayTaskDetail) => {
    try {
      await mutations.pause.mutateAsync({
        checkinId: task.checkinId,
        idempotencyKey: newIdempotencyKey(`pause:${task.checkinId}`),
      });
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const doResume = async (task: TodayTaskDetail) => {
    try {
      await mutations.resume.mutateAsync({
        checkinId: task.checkinId,
        idempotencyKey: newIdempotencyKey(`resume:${task.checkinId}`),
      });
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  /**
   * 打开「完成」弹窗。
   *
   * ⚠️ **不预填**实际完成量（这里曾经是 `String(task.targetValue)`）：
   *   预填的后果是孩子直接点「完成」= 替他填了"我做满了目标"，
   *   而这个数字要算分、要进统计。现在一律从空串开始，判定见 `lib/finishAmount.ts`。
   */
  const openFinish = (task: TodayTaskDetail) => {
    setFinishTarget({ task, mode: 'finish' });
    setFinishValue('');
    setFinishNote('');
    setActionError(null);
  };

  /**
   * 打开「补卡」弹窗 —— 与「完成」**同一个弹窗、同一套校验**。
   *
   * ⚠️ 补卡以前是**不问就记**：`valueDone: task.targetValue ?? null`。
   *   那正是同一个坑的另一半（拿目标值当实际完成量），所以补卡也改成一并填。
   */
  const openMakeup = (task: TodayTaskDetail) => {
    setFinishTarget({ task, mode: 'makeup' });
    setFinishValue('');
    setFinishNote('');
    setActionError(null);
  };

  const confirmFinish = async () => {
    if (!finishTarget) return;
    const { task, mode } = finishTarget;
    /**
     * 唯一的校验入口：有目标值就必须自己填（空着跳不过去），没目标值可以留空
     * （留空 = 按「完成一次」计入，见 `@fht/shared` 的 `calculateCheckinPoints`）。
     */
    const check = checkFinishAmount(finishValue, task.targetValue);
    if (!check.ok) {
      setActionError(check.error);
      return;
    }
    setActionError(null);
    try {
      if (mode === 'makeup') {
        await mutations.makeup.mutateAsync({
          checkinId: task.checkinId,
          valueDone: check.valueDone,
          note: finishNote.trim() || '补卡',
          idempotencyKey: newIdempotencyKey(`makeup:${task.checkinId}`),
        });
        toast.showToast('已补卡（不计入连续打卡）', 'success');
      } else {
        await mutations.finish.mutateAsync({
          checkinId: task.checkinId,
          valueDone: check.valueDone,
          note: finishNote.trim(),
          idempotencyKey: newIdempotencyKey(`finish:${task.checkinId}`),
        });
        toast.showToast('打卡完成，真棒！', 'success');
      }
      setFinishTarget(null);
    } catch (err) {
      setActionError(toErrorMessage(err));
    }
  };

  /** 点那一行的「放弃…」→ **只弹确认**，一个请求都不发（reducer 保证）。 */
  const openSkip = (task: TodayTaskDetail) => {
    setActionError(null);
    setSkipFlow((prev) => reduceSkipFlow(prev, { type: 'open', checkinId: task.checkinId, name: task.name }).state);
  };

  const cancelSkip = () => {
    setActionError(null);
    setSkipFlow((prev) => reduceSkipFlow(prev, { type: 'cancel' }).state);
  };

  /**
   * 「确定放弃」—— 唯一会真的打 `POST /checkins/:id/skip` 的地方。
   *
   * 成功后**立刻**弹一条带「撤销」按钮的 toast（`SKIP_UNDO_TOAST_MS` = 15 秒）：
   * 孩子误点了放弃，回程要在原地就能点到，而不是让他自己去找那一行。
   * 15 秒之后也不是就没了 —— 那一行还有常驻的「恢复成未完成」（今天一直可用）。
   */
  const confirmSkip = async () => {
    const step = reduceSkipFlow(skipFlow, { type: 'confirm' });
    setSkipFlow(step.state);
    // 只有 confirming 阶段的 confirm 才带副作用（双击的第二下走这里会拿到 none）
    if (step.effect.type !== 'requestSkip') return;
    const { checkinId, reason } = step.effect;
    const name = skipFlow.name;
    try {
      await mutations.skip.mutateAsync({
        checkinId,
        reason: reason.trim(),
        idempotencyKey: newIdempotencyKey(`skip:${checkinId}`),
      });
      setSkipFlow((prev) => reduceSkipFlow(prev, { type: 'submitted' }).state);
      setActionError(null);
      toast.showToast(`已放弃「${name}」`, 'info', {
        action: { label: '撤销', onClick: () => void doRestore(checkinId, name) },
        durationMs: SKIP_UNDO_TOAST_MS,
      });
    } catch (err) {
      // 失败时退回可再点的状态：把弹窗收掉会让人以为"放弃成功了"
      setSkipFlow((prev) => reduceSkipFlow(prev, { type: 'failed' }).state);
      setActionError(toErrorMessage(err));
    }
  };

  /**
   * 撤销放弃（`skipped → pending`）。
   *
   * 两个入口共用这一个函数：放弃成功后的限时 toast、以及「已放弃」那一行的常驻按钮。
   * 真实权限在服务端（孩子只能恢复自己今天的、非作业项），这里只负责把结果显示出来。
   */
  const doRestore = async (checkinId: string, name: string) => {
    try {
      const result = await mutations.restore.mutateAsync({
        checkinId,
        idempotencyKey: newIdempotencyKey(`restore:${checkinId}`),
      });
      toast.showToast(
        `「${name}」${restoreSuccessMessage({
          reversedDelta: result.reversedDelta,
          actualSeconds: result.actualSeconds,
        })}`,
        'success',
      );
    } catch (err) {
      // 403（隔天 / 作业项）与 409（状态已经变了）都走这里，文案来自 shared 的 ERROR_MESSAGES
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  return (
    <AppShell
      kind="student"
      title={kid ? '今天要做什么？' : '今日打卡'}
      compactHeader={kid}
      /* 返回箭头由 `lib/backNav.ts` 统一给出（学生端都在底部 tab 里 → 没有箭头），
         页面不再自己传 `backTo`（见该文件顶部"一个页面最多一个返回入口"的约定）。 */
      headerAction={
        <span
          className={cn(
            'rounded-xl bg-amber-50 font-bold text-amber-700',
            kid ? 'px-3 py-2 text-lg' : 'px-3 py-1.5 text-sm',
          )}
          aria-label={`当前积分 ${todayQuery.data?.pointsBalance ?? 0}`}
        >
          ⭐ {todayQuery.data?.pointsBalance ?? 0}
        </span>
      }
    >
      <div className={cn('space-y-5', kid && 'ui-child space-y-6')}>
        {/* 首登强制改密提醒：学生用的是家长转达的初始密码，
            不主动提示的话他不知道自己该改密码（导航里有"设置"但未必会点）。 */}
        {user?.mustChangePassword && (
          <Link
            to="/student/settings"
            className={cn(
              'block rounded-2xl bg-amber-50 text-amber-800 hover:bg-amber-100',
              kid ? 'px-4 py-4 text-lg' : 'px-4 py-3 text-sm',
            )}
          >
            你现在用的是<strong>家长给的初始密码</strong>，点这里改成只有你自己知道的密码 →
          </Link>
        )}

        {/* ---------- 今日进度 ---------- */}
        <section
          aria-label="今日进度"
          className={cn(
            'rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white',
            kid ? 'p-5' : 'p-4',
          )}
        >
          <p className={cn('text-brand-50', kid ? 'text-lg' : 'text-sm')}>今天完成了</p>
          <p className={cn('font-bold tabular-nums', kid ? 'text-5xl' : 'text-3xl')}>
            {doneCount}
            <span className={cn('ml-1 font-medium', kid ? 'text-2xl' : 'text-lg')}>
              / {items.length || 0} 项
            </span>
          </p>
          <p className={cn('mt-2 text-brand-50', kid ? 'text-base' : 'text-xs')}>
            {pending.length > 0
              ? `还有 ${pending.length} 项没做，一件一件来就好`
              : items.length > 0
                ? '今天的任务全部完成，太棒了！'
                : '今天还没有任务'}
          </p>
        </section>

        {/* ---------- 语文听写入口（迁移 0020 的批量任务）----------
            用户原话："孩子端要有听写入口 —— 这样就不用大人陪着。"
            ⚠️ 没有批量任务时这块**整块不渲染**（见组件文件头）：
               不给孩子一个点进去空空的入口。 */}
        <StudentDictationEntry studentId={studentId} kid={kid} />

        {/* ---------- 任务清单 ---------- */}
        {todayQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className={kid ? 'h-24 w-full' : 'h-16 w-full'} />
            <Skeleton className={kid ? 'h-24 w-full' : 'h-16 w-full'} />
          </div>
        ) : todayQuery.isError ? (
          <ErrorState message={toErrorMessage(todayQuery.error)} onRetry={() => void todayQuery.refetch()} />
        ) : notReady ? (
          <NotReadyState
            feature="今日打卡"
            description="打卡接口还在上线中。家长已经布置的目标都保存好了，接口接通后今天的清单会立刻出现。"
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={kid ? '🌱' : '📭'}
            title="今天没有要打的卡"
            description="可能是今天不在计划内（比如每周一三五的任务，今天不用做）。也可能家长还没布置目标。"
          />
        ) : (
          <section aria-labelledby="today-heading" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2
                id="today-heading"
                className={cn(
                  'font-semibold text-slate-700',
                  kid ? 'text-xl' : 'text-sm uppercase tracking-wide text-slate-500',
                )}
              >
                {kid ? '一件一件来' : '今日待打卡'}
              </h2>
              <span className="text-xs text-slate-400">{uiModeHint(mode)}</span>
            </div>

            {/*
              ---------- 已完成：默认折叠，**放在列表最上面** ----------
              ⚠️ 2026-10-30 用户要求："任务多了之后，小孩界面上看就会有点乱……不知道主次"，
                 于是已完成的卡默认收起（只留一行「已完成 N 项」）。

              ⚠️ 2026-10-30 第二次调整：这一行原来在**19 张未完成卡之后**，用户的原话是
                 "完成了打卡任务的列表里面隐藏了吗？怎么没有了？看不到了" ——
                 孩子根本滚不到那里，等于"做完的事凭空消失"。
                 现在放在**最上面**：一进页面就能看到「已完成 N 项」，点一下展开看明细；
                 收起时它只占一行，不会把没做完的卡挤下去。
            */}
            {doneItems.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  aria-expanded={showDone}
                  onClick={() => setShowDone((value) => !value)}
                  className="flex w-full items-center justify-between rounded-xl bg-emerald-50 px-3 py-2 text-left"
                >
                  <span className="text-sm font-medium text-emerald-800">
                    ✅ 已完成 {doneItems.length} 项{showDone ? '' : '（点这里看）'}
                  </span>
                  <span className="text-xs text-emerald-700">{showDone ? '收起 ▲' : '展开 ▼'}</span>
                </button>
                {showDone && <ul className="space-y-3">{doneItems.map((task) => renderTaskRow(task))}</ul>}
              </div>
            )}

            {/*
              **平铺**（2026-10-30）：顺序 = 服务端顺序 = 家长在「调整打卡任务的顺序」里拖出来的顺序。
              ⚠️ 这里曾经按节点分组渲染（`groupTodayItems`）。分组会让"跨节点的交错顺序"
                  被并成"A 的（1、2）→ B 的"，于是家长拖的顺序在孩子端永远对不上
                  —— 用户原话："我发现我对打卡任务排序后的任务排序，从孩子端界面的排序是不一样的。"
                  卡面上本来就写着「属于：XXX」，所以分组标题也一并去掉。
              ⚠️ key 仍用 `checkinId`（唯一），不要用名字。
            */}
            <ul className="space-y-3">{pending.map((task) => renderTaskRow(task))}</ul>

            {/* ---------- 全部做完：给一句明确的话 ---------- */}
            {pending.length === 0 && (
              <p className="rounded-2xl bg-emerald-50 px-4 py-3 text-base font-medium text-emerald-800">
                今天的任务全部做完了，太棒了 🎉
              </p>
            )}
          </section>
        )}

        {/* ---------- 打卡小星星（仅儿童模式，图形化进度） ---------- */}
        {kid && items.length > 0 && (
          <section aria-label="今日打卡小星星" className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-lg font-medium text-slate-700">我的小星星</p>
            <div className="mt-3 flex flex-wrap gap-3">
              {items.map((task) => (
                <span
                  key={task.checkinId}
                  aria-hidden="true"
                  title={task.name}
                  className={cn(
                    'flex h-14 w-14 items-center justify-center rounded-2xl text-3xl',
                    task.status === 'done' ? 'bg-amber-100' : 'bg-slate-100 opacity-40',
                  )}
                >
                  {task.status === 'done' ? '⭐' : '☆'}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* ---------- 儿童模式：**不显示**统计图表与导出（见方案 2.5） ---------- */}
        {!kid && (
          <Card>
            <CardHeader title="打卡怎么算时间" description={uiModeHint(mode)} />
            <ul className="list-inside list-disc space-y-1 text-sm text-slate-500">
              <li>点「开始」先填预计时长，然后开始计时；计时中可以暂停、继续或放弃。</li>
              <li>「放弃」会先问你一次；点错了也没关系，放弃之后 15 秒内可以撤销，今天之内也能在那一条下面点「恢复成未完成」。</li>
              <li>已用时间由<strong>服务端时间戳</strong>累计，改手机的时间不会影响统计。</li>
              <li>计时中每 30 秒自动上报一次心跳，用于「长时间无操作自动暂停」的判定。</li>
              <li>预计时长的两倍仍未操作，服务端会把这次打卡自动暂停，避免通宵计时。</li>
            </ul>
          </Card>
        )}
      </div>

      {/* ---------- 第 1 步：填预计时长 ---------- */}
      <Modal
        open={durationTarget !== null}
        onClose={() => setDurationTarget(null)}
        title="预计要做多久？"
        description={durationTarget?.name}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              size={kid ? 'kid' : 'md'}
              className="sm:flex-1"
              onClick={() => setDurationTarget(null)}
            >
              再想想
            </Button>
            <Button
              size={kid ? 'kid' : 'md'}
              variant={kid ? 'kid-green' : 'primary'}
              className="sm:flex-1"
              loading={mutations.start.isPending}
              onClick={() => void confirmStart()}
            >
              开始计时
            </Button>
          </div>
        }
      >
        <div className={cn('space-y-3', kid && 'ui-child')}>
          {durationEstimate?.source === 'parent-target' && durationEstimate.targetMinutes !== null && (
            <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-800">
              家长设的目标是 <strong>{durationEstimate.targetMinutes} 分钟</strong>，已经帮你填好了。
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {startEstimatePresets(durationEstimate?.targetMinutes ?? null, DURATION_PRESETS).map((minutes) => (
              <button
                key={minutes}
                type="button"
                aria-pressed={durationInput === String(minutes)}
                onClick={() => setDurationInput(String(minutes))}
                className={cn(
                  'rounded-xl font-semibold transition-colors',
                  kid ? 'min-h-[56px] px-5 text-xl' : 'px-3 py-2 text-sm',
                  durationInput === String(minutes)
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                )}
              >
                {minutes} 分钟
              </button>
            ))}
          </div>
          <Input
            label="或者直接填分钟数"
            type="number"
            min={1}
            max={720}
            kid={kid}
            value={durationInput}
            onChange={(e) => setDurationInput(e.target.value)}
            hint="最少 1 分钟，最多 12 小时"
          />
          {actionError && (
            <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {actionError}
            </p>
          )}
        </div>
      </Modal>

      {/* ---------- 第 2 步：完成 / 补卡（实际完成量**要孩子自己填**） ---------- */}
      <Modal
        open={finishTarget !== null}
        onClose={() => setFinishTarget(null)}
        title={finishTarget?.mode === 'makeup' ? '补这次卡' : '完成啦！'}
        description={finishTarget?.task.name}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              size={kid ? 'kid' : 'md'}
              className="sm:flex-1"
              onClick={() => setFinishTarget(null)}
            >
              {finishTarget?.mode === 'makeup' ? '再想想' : '还没好'}
            </Button>
            <Button
              size={kid ? 'kid' : 'md'}
              variant={kid ? 'kid-green' : 'primary'}
              className="sm:flex-1"
              loading={mutations.finish.isPending || mutations.makeup.isPending}
              onClick={() => void confirmFinish()}
            >
              {finishTarget?.mode === 'makeup' ? '确认补卡' : '确认完成'}
            </Button>
          </div>
        }
      >
        {finishTarget && (
          <div className={cn('space-y-3', kid && 'ui-child')}>
            {finishTarget.mode === 'makeup' ? (
              <p className={cn('rounded-xl bg-amber-50 px-3 py-2 text-amber-800', kid ? 'text-base' : 'text-sm')}>
                这是补之前的卡：填上你<strong>实际</strong>完成了多少再交。
              </p>
            ) : (
              <p className={cn('text-slate-600', kid ? 'text-lg' : 'text-sm')}>
                这次实际用了{' '}
                <span className="font-semibold">
                  {formatClock((finishTarget.task.actualSeconds ?? 0) || 0)}
                </span>
                。
              </p>
            )}

            {/* 「实际完成量」——**永远从空开始**，有目标值时必须自己填（校验见 confirmFinish）。
                提示走 `hint` 常驻在输入框下面（而不是把按钮置灰、也不是 `error`）：
                孩子得先知道这一格要自己填；点提交没填时，下面的红条会明确说出来。 */}
            <Input
              label={finishAmountLabel(finishTarget.task.targetValue, finishTarget.task.unit)}
              type="number"
              min={1}
              inputMode="decimal"
              kid={kid}
              value={finishValue}
              onChange={(e) => setFinishValue(e.target.value)}
              hint={finishAmountHint(finishTarget.task.targetValue)}
            />

            {/* 没设目标值的任务：不需要额外一句 —— `hint` 已经说明"留空按完成一次算" */}

            <Input
              label="想对家长说的话"
              kid={kid}
              maxLength={500}
              value={finishNote}
              onChange={(e) => setFinishNote(e.target.value)}
              placeholder="例如：今天读得很专心"
              hint="不想写就留空。"
            />

            {/*
              积分预估只在真的填了数之后才算 —— 不拿目标值替孩子预演一遍。
              ⚠️ 走 shared 的 `calculateCheckinPoints`（与后端入账**同一个函数**）：
              「每 2 分钟得 1 分」填 30 分钟是 15 分，直接乘 `pointsPerUnit` 会显示成 30 分。
            */}
            {finishTarget.task.pointsPerUnit && finishValue.trim() !== '' ? (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                💡 这次大约能得{' '}
                {calculateCheckinPoints({
                  pointsPerUnit: finishTarget.task.pointsPerUnit,
                  unitSize: finishTarget.task.pointsUnitSize,
                  targetValue: finishTarget.task.targetValue,
                  valueDone: Number(finishValue) || 0,
                })}{' '}
                分
                {finishTarget.task.dailyPointsCap !== null &&
                finishTarget.task.dailyPointsCap !== undefined
                  ? `（这项每天最多 ${finishTarget.task.dailyPointsCap} 分）`
                  : ''}
              </p>
            ) : null}

            {actionError && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {actionError}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ---------- 放弃（二次确认） ----------
          ⚠️ 这个弹窗是 2026-10-17 那次误触的**第二道防线**（第一道是按钮挪位）：
             · 文案逐字来自 `SKIP_CONFIRM_COPY`，"后果"那句明确写"今天不能再完成"；
             · **默认焦点落在"还没完，继续做"上**（`autoFocus`）—— 误触 Enter、
               误点确定键的位置，落到的都是"不放弃"；
             · 「确定放弃」**不是**与取消键并排、更不是视觉主按钮：
               它被降到下方单独一行、尺寸小一档（儿童模式 44px，取消键 56px 通栏），
               要瞄准才能点到 —— 两个大按钮并排正是这次误触的形状。
          语义（谁来兜底）：
             · 打开 / 改原因 / 取消 → **零请求**（`reduceSkipFlow` 的不变式，有单测）；
             · 只有「确定放弃」→ 一次 `POST /checkins/:id/skip`；
             · 成功后立刻弹带「撤销」的 toast（15 秒），行内另有常驻「恢复成未完成」。 */}
      <Modal
        open={skipFlow.phase !== 'idle'}
        onClose={cancelSkip}
        title={SKIP_CONFIRM_COPY.title}
        description={skipFlow.name}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-3">
            {/*
              安全选择放上面、通栏、够大（儿童模式 56px），并且是**默认焦点**。
              注意 variant 是 secondary/primary 反着用的：这里"确认"反而是次要动作，
              因为它的后果不可逆（放弃 → 这一项今天结束）。
            */}
            <Button
              variant={kid ? 'kid' : 'secondary'}
              size={kid ? 'kid' : 'md'}
              fullWidth
              autoFocus
              onClick={cancelSkip}
            >
              {SKIP_CONFIRM_COPY.cancelLabel}
            </Button>
            <div className="flex justify-center">
              <Button
                variant="danger"
                size={kid ? 'md' : 'sm'}
                loading={mutations.skip.isPending || skipFlow.phase === 'submitting'}
                onClick={() => void confirmSkip()}
              >
                {SKIP_CONFIRM_COPY.confirmLabel}
              </Button>
            </div>
          </div>
        }
      >
        <div className={cn('space-y-3', kid && 'ui-child')}>
          <p className={cn('text-slate-700', kid ? 'text-lg' : 'text-sm')}>
            {SKIP_CONFIRM_COPY.consequence}
          </p>
          <p className={cn('rounded-xl bg-amber-50 px-3 py-2 text-amber-800', kid ? 'text-base' : 'text-sm')}>
            {SKIP_CONFIRM_COPY.undoHint}
          </p>
          <Input
            label="原因（可选）"
            kid={kid}
            maxLength={200}
            value={skipFlow.reason}
            onChange={(e) =>
              setSkipFlow((prev) => reduceSkipFlow(prev, { type: 'reason', value: e.target.value }).state)
            }
            placeholder="例如：今天身体不舒服"
            hint="家长会看到这句话，不想写就留空。"
          />
          {actionError && (
            <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {actionError}
            </p>
          )}
        </div>
      </Modal>
    </AppShell>
  );
}
